<template>
  <div>
    <div v-if="loadingGetData">
      <div class="flex-1 flex items-center justify-center py-6">
        <div class="text-center">
          <svg class="animate-spin mx-auto h-10 w-10 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p class="mt-2 text-sm text-gray-500">Chargement des données...</p>
        </div>
      </div>
    </div>
    
    <div v-if="!loadingGetData" class="min-h-screen flex flex-col bg-gray-50 font-sans">
      <!-- ══ MODALE CATALOGUE ══ -->
      <div v-if="showCatalogue" class="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
        <div class="absolute inset-0 bg-black/50 backdrop-blur-[2px]" @click="showCatalogue = false"></div>

        <!-- ─────────────────────────────────────────
            DESKTOP  (sm+)  — sidebar + liste
        ───────────────────────────────────────── -->
        <div class="relative z-10 hidden sm:flex w-full max-w-2xl mx-4 rounded-2xl overflow-hidden shadow-2xl"
            style="max-height:82vh; background:#0f0f0f;">

          <!-- Sidebar catégories -->
          <aside class="w-52 flex-shrink-0 flex flex-col border-r border-white/5" style="background:#0f0f0f;">
            <div class="px-4 pt-5 pb-3 flex-shrink-0">
              <div class="flex items-center gap-2">
                <div class="w-6 h-6 rounded-lg bg-orange-500 flex items-center justify-center">
                  <i class="fas fa-bolt text-white text-xs"></i>
                </div>
                <span class="text-white text-xs font-bold tracking-wide uppercase">Catalogue</span>
              </div>
            </div>
            <nav class="flex-1 overflow-y-auto px-2 pb-3 space-y-0.5" style="scrollbar-width:none;">
              <button
                v-for="cat in catalogue" :key="cat.id"
                @click="activeCat = cat.id"
                :class="['w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left transition-all duration-150 group',
                  activeCat === cat.id
                    ? 'bg-white/10 text-white'
                    : 'text-white/40 hover:text-white/70 hover:bg-white/5']">
                <span :class="['w-8 h-8 rounded-xl flex items-center justify-center text-xs flex-shrink-0 transition-all',
                  activeCat === cat.id ? cat.colorClass : 'bg-white/5 group-hover:bg-white/10']">
                  <i :class="cat.icon"></i>
                </span>
                <span class="text-xs font-semibold leading-tight flex-1">{{ cat.nom }}</span>
                <i v-if="activeCat === cat.id" class="fas fa-chevron-right text-orange-400" style="font-size:9px;"></i>
              </button>
            </nav>

            <!-- Ligne personnalisée en bas sidebar -->
            <div class="p-3 border-t border-white/5 flex-shrink-0">
              <button @click="addLineVierge"
                class="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl border border-dashed border-white/15 hover:border-orange-500/50 hover:bg-orange-500/5 text-white/30 hover:text-orange-400 transition-all duration-150">
                <i class="fas fa-pencil-alt text-xs"></i>
                <span class="text-xs font-medium">Ligne libre</span>
              </button>
            </div>
          </aside>

          <!-- Panneau items -->
          <div class="flex flex-col flex-1 min-w-0 bg-white">

            <!-- Header -->
            <div class="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
              <div v-for="cat in catalogue" :key="cat.id">
                <div v-if="activeCat === cat.id" class="flex items-center gap-2.5">
                  <span :class="['w-8 h-8 rounded-xl flex items-center justify-center text-xs', cat.colorClass]">
                    <i :class="cat.icon"></i>
                  </span>
                  <span class="font-bold text-gray-900 text-sm">{{ cat.nom }}</span>
                  <span class="text-xs text-gray-400 font-medium bg-gray-100 px-2 py-0.5 rounded-full">
                    {{ cat.items.length }} prestations
                  </span>
                </div>
              </div>
              <button @click="showCatalogue = false"
                class="w-8 h-8 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-400 transition-colors">
                <i class="fas fa-times text-xs"></i>
              </button>
            </div>

            <!-- Liste -->
            <div class="overflow-y-auto flex-1 p-3 space-y-1" style="scrollbar-width:none;-webkit-overflow-scrolling:touch;">
              <div v-for="cat in catalogue" :key="cat.id">
                <div v-if="activeCat === cat.id">
                  <button
                    v-for="item in cat.items" :key="item.designation"
                    @click="addFromCatalogue(item)"
                    class="w-full flex items-center gap-3 px-3 py-3 rounded-xl border border-transparent hover:bg-gray-50 hover:border-gray-100 text-left transition-all group">
                    <div class="flex-1 min-w-0">
                      <p class="font-semibold text-gray-900 text-sm leading-tight">{{ item.designation }}</p>
                      <p class="text-xs text-gray-400 mt-0.5 flex items-center gap-1.5">
                        <span v-if="item.pu_ht > 0" class="font-medium text-gray-500">{{ item.pu_ht }} € / {{ item.unite }}</span>
                        <span v-else class="italic">Prix à saisir</span>
                        <span class="w-0.5 h-0.5 rounded-full bg-gray-300 inline-block"></span>
                        <span>TVA {{ item.tva }}%</span>
                      </p>
                    </div>
                    <div class="w-7 h-7 rounded-lg border border-gray-200 bg-white group-hover:bg-orange-500 group-hover:border-orange-500 flex items-center justify-center flex-shrink-0 transition-all duration-150">
                      <i class="fas fa-plus text-gray-300 group-hover:text-white transition-colors" style="font-size:10px;"></i>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ─────────────────────────────────────────
            MOBILE  (< sm)  — bottom sheet
        ───────────────────────────────────────── -->
        <div class="relative z-10 sm:hidden w-full rounded-t-3xl bg-white flex flex-col overflow-hidden catalogue-sheet"
            style="max-height:88vh;">

          <!-- Handle -->
          <div class="flex justify-center pt-3 pb-0 flex-shrink-0">
            <div class="w-9 h-1 rounded-full bg-gray-200"></div>
          </div>

          <!-- Header compact -->
          <div class="flex items-center justify-between px-4 pt-3 pb-2 flex-shrink-0">
            <span class="font-bold text-gray-900 text-sm flex items-center gap-1.5">
              <i class="fas fa-bolt text-orange-400"></i>Ajouter une prestation
            </span>
            <button @click="showCatalogue = false"
              class="w-8 h-8 rounded-xl bg-gray-100 flex items-center justify-center text-gray-400 active:bg-gray-200">
              <i class="fas fa-times text-xs"></i>
            </button>
          </div>

          <!-- Pills catégories -->
          <div class="flex gap-2 px-4 py-2 overflow-x-auto flex-shrink-0" style="scrollbar-width:none;-webkit-overflow-scrolling:touch;">
            <button
              v-for="cat in catalogue" :key="cat.id"
              @click="activeCat = cat.id"
              :class="['flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all',
                activeCat === cat.id
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-500 active:bg-gray-200']">
              <i :class="[cat.icon, 'text-xs']"></i>{{ cat.nom }}
            </button>
          </div>

          <!-- Séparateur -->
          <div class="h-px bg-gray-100 mx-4 flex-shrink-0"></div>

          <!-- Items -->
          <div class="overflow-y-auto flex-1 px-3 py-2 space-y-0.5" style="-webkit-overflow-scrolling:touch;">
            <div v-for="cat in catalogue" :key="cat.id">
              <div v-if="activeCat === cat.id">
                <button
                  v-for="item in cat.items" :key="item.designation"
                  @click="addFromCatalogue(item)"
                  class="w-full flex items-center gap-3 px-3 py-3 active:bg-orange-50 rounded-2xl text-left transition-all">
                  <div :class="['w-10 h-10 rounded-xl flex items-center justify-center text-sm flex-shrink-0', cat.colorClass]">
                    <i :class="cat.icon"></i>
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="font-semibold text-gray-900 text-sm leading-snug">{{ item.designation }}</p>
                    <p class="text-xs text-gray-400 mt-0.5">
                      <span v-if="item.pu_ht > 0">{{ item.pu_ht }} € / {{ item.unite }}</span>
                      <span v-else class="italic">Prix à saisir</span>
                      · TVA {{ item.tva }}%
                    </p>
                  </div>
                  <div class="w-8 h-8 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <i class="fas fa-plus text-gray-400" style="font-size:10px;"></i>
                  </div>
                </button>
              </div>
            </div>
          </div>

          <!-- Ligne personnalisée collée en bas -->
          <div class="px-4 py-3 border-t border-gray-100 flex-shrink-0">
            <button @click="addLineVierge"
              class="w-full py-3 border-2 border-dashed border-gray-200 active:border-orange-300 active:bg-orange-50 rounded-2xl flex items-center justify-center gap-2 text-xs text-gray-400 active:text-orange-500 font-medium transition-all">
              <i class="fas fa-pencil-alt"></i>Ligne personnalisée
            </button>
          </div>
        </div>

      </div>

      <!-- ══ BARRE DU HAUT ══ -->
      <div class="bg-white border-b border-gray-200 px-4 sm:px-6 py-2.5 sm:py-3 flex-shrink-0 shadow-sm">
        <!-- Mobile: deux rangées -->
        <div class="flex items-center justify-between gap-2 sm:gap-0">
          <!-- Onglets saisie/consulter -->
          <div class="flex bg-gray-100 rounded-xl p-1 gap-0.5">
            <button @click="activeTab = 'saisie'"
              :class="['px-3 sm:px-5 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5',
                activeTab === 'saisie' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-400']">
              <i class="fas fa-pencil-alt text-xs"></i>Saisie
            </button>
            <button @click="activeTab = 'consulter'"
              :class="['px-3 sm:px-5 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5',
                activeTab === 'consulter' ? 'bg-white text-orange-500 shadow-sm' : 'text-gray-400']">
              <i class="fas fa-eye text-xs"></i>Aperçu
            </button>
          </div>
        </div>
      </div>

      <!-- ══ CORPS ══ -->
      <div class="flex flex-1 overflow-hidden">

        <!-- ── VUE SAISIE ── -->
        <div v-show="activeTab === 'saisie'" class="flex-1 overflow-y-auto saisie-scroll">
          <div class="mx-auto py-4 sm:py-6 px-4 sm:px-6">

            <!-- CLIENT + DATES ── responsive grid -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-5 sm:mb-6">
              <div class="grid grid-cols-3 gap-3 md:col-span-2">
                <div>
                  <label class="mobile-label"><i class="fas fa-file-alt mr-1"></i>Type</label>
                  <select v-model="doc.type" class="ipt ipt-mobile">
                    <option value="DEVIS">Devis</option>
                    <option value="FACTURE">Facture</option>
                  </select>
                </div>
                <div>
                  <label class="mobile-label"><i class="fas fa-calendar mr-1"></i>Émission</label>
                  <input type="date" v-model="doc.date_emission" class="ipt ipt-mobile" />
                </div>
                <div>
                  <label class="mobile-label">
                    <i class="fas fa-calendar-check mr-1"></i>{{ doc.type === 'FACTURE' ? 'Échéance paiement' : "Valide jusqu'au" }}
                  </label>
                  <input type="date" v-model="doc.date_validite" class="ipt ipt-mobile" />
                </div>
              </div>
            </div>

            <!-- ── SECTION PRESTATIONS ── -->
            <div class="flex items-center justify-between mb-3">
              <span class="text-xs font-black text-gray-400 uppercase tracking-wider">
                <i class="fas fa-list mr-1"></i> Lignes {{ doc.type === 'DEVIS' ? 'du devis' : 'de la facture' }}
                <span v-if="doc.lignes.length" class="ml-1 bg-orange-100 text-orange-600 rounded-full px-1.5 py-0.5 text-xs font-bold">{{ doc.lignes.length }}</span>
              </span>
              <!-- <button v-if="catalogue.length" @click="openCatalogue"
                class="hidden sm:flex items-center gap-1.5 text-xs px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-lg transition-colors shadow-sm">
                <i class="fas fa-plus text-xs"></i>Ajouter
              </button>
              <button v-else @click="addLineVierge" class="hidden sm:flex items-center gap-1.5 text-xs px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-lg transition-colors shadow-sm">
                <i class="fas fa-plus text-xs"></i>Nouvelle ligne
              </button> -->
            </div>

            <!-- ════ DESKTOP TABLE (masqué sur mobile) ════ -->
            <div class="hidden sm:block mb-5">
              <div class="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                <!-- Header 8 colonnes -->
                <div class="grid bg-slate-800 text-white" style="grid-template-columns: minmax(140px,300px) minmax(68px,1fr) minmax(100px,1fr) minmax(90px,1fr) minmax(100px,1fr) minmax(66px,1fr) minmax(88px,1fr) 32px">
                  <div class="px-3 py-2 text-xs font-bold">Désignation</div>
                  <div class="px-2 py-2 text-xs font-bold text-center">
                    <span>Qté</span>
                    <span class="text-gray-400 font-normal block" style="font-size:10px">Unité</span>
                  </div>
                  <div class="px-2 py-2 text-center">
                    <span class="text-xs font-bold flex items-center justify-center gap-1">
                      Coût HT
                      <span class="relative group/tip cursor-default">
                        <i class="fas fa-circle-question text-gray-400 hover:text-orange-400 transition-colors" style="font-size:10px"></i>
                        <span class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-52 bg-gray-900 text-white text-xs rounded-xl px-3 py-2.5 leading-relaxed opacity-0 group-hover/tip:opacity-100 transition-opacity pointer-events-none z-50 shadow-xl text-left font-normal normal-case tracking-normal">
                          Ce que la prestation <strong>coûte à l'entreprise</strong> : salaire chargé du technicien, coût d'achat du matériel…<br/>
                          <span class="text-gray-400 mt-1 block">La marge se calcule ensuite par-dessus.</span>
                        </span>
                      </span>
                    </span>
                    <span class="text-gray-400 font-normal" style="font-size:10px">Prix de revient</span>
                  </div>
                  <div class="px-2 py-2 text-center">
                    <span class="text-xs font-bold block">Marge</span>
                    <span class="text-gray-400 font-normal" style="font-size:10px">% ou €</span>
                  </div>
                  <div class="px-2 py-2 text-center">
                    <span class="text-xs font-bold block">PU HT</span>
                    <span class="text-gray-400 font-normal" style="font-size:10px">Prix de vente</span>
                  </div>
                  <div class="px-2 py-2 text-xs font-bold text-center">TVA</div>
                  <div class="px-2 py-2 text-center">
                    <span class="text-xs font-bold block">Total TTC</span>
                    <div class="text-gray-400 font-normal" style="font-size:10px">Dont &nbsp;HT</div>
                  </div>
                  <div></div>
                </div>

                <!-- Vide -->
                <div v-if="doc.lignes.length === 0" class="py-8 text-center">
                  <!-- <button v-if="catalogue.length" @click="openCatalogue" class="text-xs text-orange-500 hover:text-orange-600 font-bold">
                    <i class="fas fa-plus-circle mr-1"></i>Ouvrir le catalogue
                  </button> -->
                  <button @click="addLineVierge" class="text-xs text-orange-500 hover:text-orange-600 font-bold">
                    <i class="fas fa-plus-circle mr-1"></i>Nouvelle ligne vierge
                  </button>
                </div>

                <!-- Lignes -->
                <div class="divide-y divide-gray-100">
                  <div v-for="(l, idx) in doc.lignes" :key="l.id"
                    :class="['grid group transition-colors', l.is_remise ? 'bg-emerald-50/50' : 'hover:bg-orange-50/20']"
                    style="grid-template-columns: minmax(140px,300px) minmax(68px,1fr) minmax(100px,1fr) minmax(90px,1fr) minmax(100px,1fr) minmax(66px,1fr) minmax(88px,1fr) 32px">

                    <div class="px-3 py-2 border-r border-gray-100">
                      <input v-model="l.designation" :class="['ti w-full font-semibold', l.is_remise ? 'text-emerald-700' : 'text-gray-900']" placeholder="Désignation…" />
                      <input v-model="l.detail" class="ti w-full text-gray-400 mt-0.5" style="font-size:11px" placeholder="Détail…" />
                    </div>

                    <div class="px-2 py-2 border-r border-gray-100">
                      <input v-model.number="l.qte" type="number" min="0" step="0.5" class="ti w-full text-center font-bold" @input="calcFromCout(l)" />
                      <select v-model="l.unite" class="ti w-full text-center cursor-pointer mt-1" style="font-size:12px!important" @change="calcFromCout(l)">
                        <option>h</option><option>j</option><option>m²</option><option>m</option><option>ml</option><option value="u">unité</option><option>forfait</option><option>kg</option>
                      </select>
                    </div>

                    <div class="px-2 py-2 border-r border-gray-100">
                      <div class="relative">
                        <input v-model.number="l.cout_ht" type="number" min="0" step="0.01" class="ti w-full text-right" style="padding-right:14px" placeholder="0" @input="calcFromCout(l)" />
                        <span class="unit-sym">€</span>
                      </div>
                    </div>

                    <div class="px-2 py-2 border-r border-gray-100">
                      <div class="relative">
                        <input v-if="l.marge_mode === 'pct'" v-model.number="l.marge_pct" type="number" min="0" step="0.1"
                          :class="['ti w-full text-right', l.marge_pct > 0 ? 'text-emerald-600 font-bold' : '']"
                          style="padding-right:14px" placeholder="0" @input="calcFromMarge(l)" />
                        <input v-else v-model.number="l.marge_eur" type="number" min="0" step="0.01"
                          :class="['ti w-full text-right', l.marge_eur > 0 ? 'text-emerald-600 font-bold' : '']"
                          style="padding-right:14px" placeholder="0" @input="calcFromMargeEur(l)" />
                        <span class="unit-sym">{{ l.marge_mode === 'pct' ? '%' : '€' }}</span>
                      </div>
                      <div class="flex gap-0.5 mt-1">
                        <button @click="l.marge_mode = 'pct'; calcFromMarge(l)" :class="['mode-btn flex-1', l.marge_mode === 'pct' ? 'mode-btn-on' : 'mode-btn-off']">%</button>
                        <button @click="l.marge_mode = 'eur'; calcFromMargeEur(l)" :class="['mode-btn flex-1', l.marge_mode === 'eur' ? 'mode-btn-on' : 'mode-btn-off']">€</button>
                      </div>
                      <p v-if="l.marge_montant > 0" class="text-right text-emerald-600 font-semibold tabular-nums mt-0.5" style="font-size:10px">+{{ fmt(l.marge_montant) }} €</p>
                    </div>

                    <div class="px-2 py-2 border-r border-gray-100">
                      <div class="relative">
                        <input v-model.number="l.pu_ht" type="number" min="0" step="0.01" class="ti w-full text-right font-bold text-gray-900" style="padding-right:14px" placeholder="0" @input="calcFromPrix(l)" />
                        <span class="unit-sym">€</span>
                      </div>
                    </div>

                    <div class="px-2 py-2 border-r border-gray-100 flex items-start">
                      <select v-model.number="l.tva" class="ti w-full text-center cursor-pointer" @change="calcFromCout(l)">
                        <option :value="0">0 % — Exonéré (auto-liquidation, franchise…)</option>
                        <option :value="5.5">5,5 % — Rénovation énergétique</option>
                        <option :value="10">10 % — Travaux de rénovation (main d'œuvre + fournitures liées)</option>
                        <option :value="20">20 % — Vente de pièces / matériel / neuf</option>
                      </select>
                    </div>

                    <div class="px-2 py-2 border-r border-gray-100 text-right">
                      <p :class="['font-black tabular-nums', l.is_remise ? 'text-emerald-600' : 'text-gray-900']" style="font-size:12px">{{ fmt(l.total_ttc) }} €</p>
                      <div class="text-gray-400 tabular-nums mt-0.5" style="font-size:10px">{{ fmt(l.total_ht) }} € HT</div>
                    </div>

                    <div class="flex flex-col items-center justify-center gap-1 px-1">
                        <button
                            v-if="!l.is_remise"
                            @click.stop="openCatalogueModal(l)"
                            title="Ajouter au catalogue"
                            class="opacity-0 group-hover:opacity-100 w-7 h-7 flex items-center justify-center rounded-lg text-gray-300 hover:text-orange-500 hover:bg-orange-50 transition-all">
                            <i class="fas fa-bookmark" style="font-size:10px;"></i>
                        </button>
                        <button @click="removeLine(idx)"
                            class="opacity-0 group-hover:opacity-100 w-7 h-7 flex items-center justify-center rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all">
                            <i class="fas fa-times text-xs"></i>
                        </button>
                    </div>
                  </div>
                </div>

                <!-- Footer raccourcis desktop -->
                <div class="border-t border-gray-100 px-3 py-2 flex items-center gap-4 bg-gray-50">
                  <button @click="addLineVierge"
                    class="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-lg transition-colors shadow-sm">
                    <i class="fas fa-plus text-xs"></i>Nouvelle ligne
                  </button>
                  <div class="w-px h-4 bg-gray-200"></div>
                  <button v-if="catalogue.length" @click="openCatalogue" class="shortcut-btn"><i class="fas fa-bolt"></i>Catalogue</button>
                  <button @click="addLine('prestation')" class="shortcut-btn"><i class="fas fa-clock"></i>Main d'œuvre</button>
                  <button @click="addLine('materiau')" class="shortcut-btn"><i class="fas fa-box"></i>Fourniture</button>
                </div>
              </div>
            </div>

            <!-- ════ MOBILE CARDS (masqué sur desktop) ════ -->
            <div class="sm:hidden mb-5">

              <!-- Chargement facture -->
              <div v-if="loadingInvoice" class="bg-white border border-gray-200 rounded-2xl py-10 text-center">
                <i class="fas fa-circle-notch fa-spin text-2xl text-orange-400 mb-3 block"></i>
                <p class="text-gray-400 text-sm font-medium">Chargement du document…</p>
              </div>

              <!-- État vide -->
              <div v-else-if="doc.lignes.length === 0" class="bg-white border-2 border-dashed border-gray-200 rounded-3xl py-12 text-center">
                <div class="w-20 h-20 bg-orange-50 rounded-3xl flex items-center justify-center mx-auto mb-4">
                  <i class="fas fa-clipboard-list text-3xl text-orange-300"></i>
                </div>
                <p class="text-gray-500 font-semibold mb-1">Aucune prestation</p>
                <p v-if="catalogue.length" class="text-gray-400 text-sm mb-6">Ajoutez des lignes depuis le catalogue</p>
                <p v-else class="text-gray-400 text-sm mb-6">Ajoutez des lignes à votre document</p>

                <button v-if="catalogue.length" @click="openCatalogue"
                  class="px-8 py-4 bg-orange-500 text-white font-black rounded-2xl text-base shadow-lg shadow-orange-100 active:bg-orange-600 transition-colors">
                  <i class="fas fa-plus mr-2"></i>Ouvrir le catalogue
                </button>
                <button v-else @click="addLineVierge" class="px-8 py-4 bg-orange-500 text-white font-black rounded-2xl text-base shadow-lg shadow-orange-100 active:bg-orange-600 transition-colors">
                  <i class="fas fa-plus mr-2"></i>Nouvelle ligne vierge
                </button>
              </div>

              <!-- ── Cartes lignes ── -->
              <div v-else class="space-y-3">
                <div v-for="(l, idx) in doc.lignes" :key="l.id"
                  :class="['bg-white rounded-2xl overflow-hidden shadow-sm border', l.is_remise ? 'border-emerald-200' : 'border-gray-200']">

                  <!-- ①  Désignation + suppr -->
                  <div :class="['px-4 pt-4 pb-3 flex items-start gap-3', l.is_remise ? 'bg-emerald-50/60' : '']">
                    <div :class="['w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5',
                      l.is_remise ? 'bg-emerald-100' : 'bg-orange-100']">
                      <i :class="['text-base', l.is_remise ? 'fas fa-percent text-emerald-600' : 'fas fa-wrench text-orange-500']"></i>
                    </div>
                    <div class="flex-1 min-w-0">
                      <input v-model="l.designation"
                        :class="['mobile-desig-input', l.is_remise ? 'text-emerald-700' : 'text-gray-900']"
                        placeholder="Désignation…" />
                      <input v-model="l.detail"
                        class="w-full text-sm text-gray-400 bg-transparent border-none outline-none mt-2 placeholder-gray-300"
                        placeholder="Détail / description…" />
                    </div>
                    <button @click="removeLine(idx)"
                      class="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-xl bg-red-50 text-red-400 active:bg-red-100 transition-colors">
                      <i class="fas fa-trash-alt text-base"></i>
                    </button>
                  </div>

                  <!-- ②  Quantité + Unité -->
                  <div class="px-4 py-3 border-t border-gray-100">
                    <p class="mobile-field-label">Quantité</p>
                    <div class="flex items-center gap-3 mt-2">
                      <div class="flex items-center bg-gray-100 rounded-2xl overflow-hidden">
                        <button @click="decrementQty(l)"
                          class="w-14 h-14 flex items-center justify-center text-gray-700 active:bg-gray-200 font-bold text-2xl select-none">−</button>
                        <input v-model.number="l.qte" type="number" min="0" step="0.5"
                          class="w-16 h-14 text-center font-black text-xl bg-transparent border-none outline-none text-gray-900 tabular-nums"
                          @input="calcFromCout(l)" />
                        <button @click="incrementQty(l)"
                          class="w-14 h-14 flex items-center justify-center text-gray-700 active:bg-gray-200 font-bold text-2xl select-none">+</button>
                      </div>
                      <select v-model="l.unite"
                        class="flex-1 h-14 px-4 bg-gray-100 border-none rounded-2xl text-base font-bold text-gray-700 outline-none cursor-pointer"
                        @change="calcFromCout(l)">
                        <option>h</option><option>j</option><option>m²</option><option>m</option>
                        <option>ml</option><option value="u">unité</option><option>forfait</option><option>kg</option>
                      </select>
                    </div>
                  </div>

                  <!-- ══ BLOC COÛT → MARGE → PRIX (cascade) ══ -->
                  <!-- Lignes normales uniquement, pas les remises -->
                  <template v-if="!l.is_remise">

                    <!-- ③  Coût HT (prix d'achat) -->
                    <div class="px-4 py-3 border-t border-gray-100">
                      <p class="mobile-field-label">Prix d'achat HT <span class="text-gray-300 font-normal normal-case tracking-normal">(optionnel)</span></p>
                      <div class="relative mt-2">
                        <input v-model.number="l.cout_ht" type="number" min="0" step="0.01"
                          class="w-full h-14 pr-10 pl-4 bg-gray-100 border-2 border-transparent rounded-2xl text-xl font-bold text-gray-700 outline-none text-right tabular-nums focus:border-gray-300 transition-colors"
                          placeholder="0,00" @input="calcFromCout(l)" />
                        <span class="absolute right-4 top-4 text-base font-bold text-gray-400">€</span>
                      </div>
                    </div>

                    <!-- flèche cascade -->
                    <div class="flex items-center px-6 py-1">
                      <div class="flex-1 border-t border-dashed border-gray-200"></div>
                      <div class="mx-3 w-7 h-7 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <i class="fas fa-arrow-down text-orange-400" style="font-size:11px"></i>
                      </div>
                      <div class="flex-1 border-t border-dashed border-gray-200"></div>
                    </div>

                    <!-- ④  Marge -->
                    <div class="px-4 pb-3">
                      <div class="flex items-center justify-between mb-2">
                        <p class="mobile-field-label">Marge</p>
                        <!-- Toggle %/€ -->
                        <div class="flex bg-gray-100 rounded-xl p-0.5">
                          <button @click="l.marge_mode = 'pct'; calcFromMarge(l)"
                            :class="['px-4 py-1.5 rounded-lg text-sm font-black transition-all', l.marge_mode === 'pct' ? 'bg-white text-orange-500 shadow-sm' : 'text-gray-400']">%</button>
                          <button @click="l.marge_mode = 'eur'; calcFromMargeEur(l)"
                            :class="['px-4 py-1.5 rounded-lg text-sm font-black transition-all', l.marge_mode === 'eur' ? 'bg-white text-orange-500 shadow-sm' : 'text-gray-400']">€</button>
                        </div>
                      </div>
                      <div class="relative">
                        <input v-if="l.marge_mode === 'pct'" v-model.number="l.marge_pct" type="number" min="0" step="0.1"
                          class="w-full h-14 pr-10 pl-4 bg-gray-100 border-2 border-transparent rounded-2xl text-xl font-bold outline-none text-right tabular-nums focus:border-emerald-300 transition-colors"
                          :class="l.marge_pct > 0 ? 'text-emerald-600' : 'text-gray-700'"
                          placeholder="0" @input="calcFromMarge(l)" />
                        <input v-else v-model.number="l.marge_eur" type="number" min="0" step="0.01"
                          class="w-full h-14 pr-10 pl-4 bg-gray-100 border-2 border-transparent rounded-2xl text-xl font-bold outline-none text-right tabular-nums focus:border-emerald-300 transition-colors"
                          :class="l.marge_eur > 0 ? 'text-emerald-600' : 'text-gray-700'"
                          placeholder="0" @input="calcFromMargeEur(l)" />
                        <span class="absolute right-4 top-4 text-base font-bold" :class="(l.marge_pct > 0 || l.marge_eur > 0) ? 'text-emerald-400' : 'text-gray-400'">
                          {{ l.marge_mode === 'pct' ? '%' : '€' }}
                        </span>
                      </div>
                      <!-- Montant marge calculé -->
                      <p v-if="l.marge_montant > 0" class="text-right text-emerald-600 font-bold tabular-nums mt-1.5 text-sm">
                        = +{{ fmt(l.marge_montant) }} € de marge
                      </p>
                    </div>

                    <!-- flèche cascade -->
                    <div class="flex items-center px-6 py-1">
                      <div class="flex-1 border-t border-dashed border-gray-200"></div>
                      <div class="mx-3 w-7 h-7 bg-orange-400 rounded-full flex items-center justify-center flex-shrink-0">
                        <i class="fas fa-arrow-down text-white" style="font-size:11px"></i>
                      </div>
                      <div class="flex-1 border-t border-dashed border-gray-200"></div>
                    </div>

                    <!-- ⑤  Prix de vente HT (calculé, mais modifiable) -->
                    <div class="px-4 pb-3">
                      <div class="flex items-center justify-between mb-2">
                        <p class="mobile-field-label">Prix de vente HT</p>
                        <span class="text-xs text-gray-300 font-medium">modifiable directement</span>
                      </div>
                      <div class="relative">
                        <input v-model.number="l.pu_ht" type="number" min="0" step="0.01"
                          class="w-full h-16 pr-10 pl-4 bg-orange-50 border-2 border-orange-200 rounded-2xl text-2xl font-black text-orange-600 outline-none text-right tabular-nums focus:border-orange-400 transition-colors"
                          placeholder="0,00" @input="calcFromPrix(l)" />
                        <span class="absolute right-4 top-5 text-base font-black text-orange-400">€</span>
                      </div>
                    </div>

                  </template>

                  <!-- Ligne remise : juste le montant -->
                  <template v-else>
                    <div class="px-4 py-3 border-t border-gray-100">
                      <p class="mobile-field-label">Montant remise</p>
                      <div class="relative mt-2">
                        <input v-model.number="l.pu_ht" type="number" step="0.01"
                          class="w-full h-14 pr-10 pl-4 bg-emerald-50 border-2 border-emerald-200 rounded-2xl text-xl font-black text-emerald-600 outline-none text-right tabular-nums focus:border-emerald-400 transition-colors"
                          @input="calcFromPrix(l)" />
                        <span class="absolute right-4 top-4 text-base font-bold text-emerald-400">€</span>
                      </div>
                    </div>
                  </template>

                  <!-- ⑥  TVA -->
                  <div class="px-4 py-3 border-t border-gray-100">
                    <p class="mobile-field-label">TVA</p>
                    <select v-model.number="l.tva"
                      class="w-full h-14 px-4 mt-2 bg-gray-100 border-none rounded-2xl text-base font-bold text-gray-700 outline-none cursor-pointer"
                      @change="calcFromCout(l)">
                      <option :value="0">0 % — exonéré</option>
                      <option :value="5.5">5,5 % — taux réduit</option>
                      <option :value="10">10 % — taux intermédiaire</option>
                      <option :value="20">20 % — taux normal</option>
                    </select>
                  </div>

                  <!-- ⑦  Total récap -->
                  <div :class="['px-4 py-4 border-t flex items-center justify-between',
                    l.is_remise ? 'bg-emerald-50/40 border-emerald-100' : 'bg-orange-50/40 border-orange-100']">
                    <span class="text-xs font-bold text-gray-400 uppercase tracking-wider">Total ligne</span>
                    <div class="text-right">
                      <p :class="['font-black text-xl tabular-nums', l.is_remise ? 'text-emerald-600' : 'text-orange-500']">
                        {{ fmt(l.total_ttc) }} € <span class="text-sm font-bold">TTC</span>
                      </p>
                      <p class="text-sm text-gray-400 tabular-nums mt-0.5">{{ fmt(l.total_ht) }} € HT</p>
                    </div>
                  </div>

                </div>
              </div>

              <!-- Bouton ajouter (si des lignes existent déjà) -->
              <button v-if="doc.lignes.length > 0 && !loadingInvoice" @click="openCatalogue"
                class="mt-3 w-full py-5 border-2 border-dashed border-orange-200 active:border-orange-400 bg-orange-50/60 rounded-2xl flex items-center justify-center gap-2 text-orange-500 font-bold text-base active:bg-orange-100 transition-colors">
                <i class="fas fa-plus-circle text-lg"></i> {{ catalogue.length ? 'Ajouter une prestation' : 'Nouvelle ligne vierge' }}
              </button>
            </div>

            <!-- ── REMISE GLOBALE ── -->
            <div class="mb-5 sm:mb-6">
              <div v-if="!showRemise">
                <button @click="showRemise = true"
                  class="text-xs text-gray-400 active:text-orange-500 font-medium transition-colors flex items-center gap-1.5 py-1">
                  <i class="fas fa-percent text-xs"></i>Ajouter une remise globale
                </button>
              </div>
              <div v-else class="bg-white border border-dashed border-orange-200 rounded-2xl p-4 flex items-start gap-3">
                <div class="w-9 h-9 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i class="fas fa-percent text-orange-500 text-sm"></i>
                </div>
                <div class="flex-1 min-w-0">
                  <input v-model="remiseLabel" class="ipt text-sm mb-3" placeholder="Libellé (ex: Remise fidélité)" />
                  <div class="flex flex-wrap gap-2 items-center">
                    <div class="flex bg-gray-100 rounded-xl p-0.5">
                      <button v-for="m in ['pct', 'ht']" :key="m" @click="remiseMode = m"
                        :class="['px-4 py-2 rounded-lg text-xs font-bold transition-all', remiseMode === m ? 'bg-white text-orange-500 shadow-sm' : 'text-gray-400']">
                        {{ m === 'pct' ? '%' : '€ HT' }}
                      </button>
                    </div>
                    <div class="relative w-28">
                      <input v-model.number="remiseValeur" type="number" min="0" step="0.01" class="ipt ipt-mobile pr-7" :placeholder="remiseMode === 'pct' ? '10' : '50'" />
                      <span class="absolute right-2.5 top-3 text-xs text-gray-400">{{ remiseMode === 'pct' ? '%' : '€' }}</span>
                    </div>
                    <span class="text-sm font-bold text-orange-600 tabular-nums">−{{ fmt(remiseMontantHT) }} € HT</span>
                  </div>
                  <div class="flex gap-2 mt-3">
                    <button @click="applyRemise"
                      class="flex-1 sm:flex-none px-4 py-2.5 bg-orange-500 active:bg-orange-600 text-white text-sm font-bold rounded-xl transition-colors">
                      Appliquer
                    </button>
                    <button @click="showRemise = false" class="px-4 py-2.5 bg-gray-100 text-gray-500 text-sm font-bold rounded-xl">
                      Annuler
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- ── TOTAUX + MARGES ── -->
            <div class="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:justify-end mb-5 sm:mb-6">
              <!-- Synthèse marges -->
              <div v-if="totalCoutHT > 0" class="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm sm:min-w-[200px]">
                <div class="bg-emerald-500 px-4 py-3 sm:py-3 flex sm:block items-center gap-4 sm:gap-0">
                  <div>
                    <p class="text-xs text-emerald-100 font-bold uppercase tracking-wider mb-0.5">Marge totale</p>
                    <p class="text-2xl sm:text-3xl font-black text-white tabular-nums leading-none">{{ fmt(totalMargeHT) }} <span class="text-xl">€</span></p>
                  </div>
                  <p class="text-emerald-200 font-bold tabular-nums text-sm sm:mt-1">{{ tauxMarge }}%</p>
                </div>
                <div class="px-4 py-3 space-y-1.5 text-xs flex sm:block justify-between sm:justify-start gap-6 sm:gap-0">
                  <div class="flex justify-between text-gray-500 sm:w-full">
                    <span>Coût total HT</span>
                    <span class="tabular-nums font-medium ml-2">{{ fmt(totalCoutHT) }} €</span>
                  </div>
                  <div class="flex justify-between text-gray-500 sm:w-full">
                    <span>CA HT</span>
                    <span class="tabular-nums font-medium ml-2">{{ fmt(totalHT) }} €</span>
                  </div>
                </div>
              </div>

              <!-- Totaux facture -->
              <div class="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm sm:min-w-[200px]">
                <div class="flex justify-between text-gray-500 text-xs mb-1.5">
                  <span>Total HT</span>
                  <span class="tabular-nums font-medium text-gray-700">{{ fmt(totalHT) }} €</span>
                </div>
                <div v-for="t in tvaDetail" :key="t.taux" class="flex justify-between text-gray-400 text-xs mb-1.5">
                  <span>TVA {{ t.taux }}%</span>
                  <span class="tabular-nums">{{ fmt(t.montant) }} €</span>
                </div>
                <div class="pt-2.5 border-t border-gray-100 flex sm:block items-center justify-between sm:justify-start">
                  <span class="font-black text-gray-900 text-sm sm:block">Total TTC</span>
                  <div class="font-black text-orange-500 text-2xl sm:text-2xl tabular-nums">{{ fmt(totalTTC) }} €</div>
                </div>
              </div>
            </div>

            <!-- ── PAIEMENT + NOTES ── -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div>
                <label class="mobile-label"><i class="fas fa-university mr-1"></i>Paiement</label>
                <div class="space-y-3">
                  <select v-model="doc.paiement.mode" class="ipt ipt-mobile">
                    <option value="VIREMENT">Virement bancaire</option>
                    <option value="CHEQUE">Chèque</option>
                    <option value="ESPECES">Espèces</option>
                    <option value="CB">Carte bancaire</option>
                  </select>
                  <transition name="fade">
                      <div v-if="doc.paiement.mode === 'VIREMENT' && !societe.iban"
                          class="flex items-start gap-2.5 bg-amber-50 border border-amber-300 rounded-xl px-3 py-2.5 text-xs text-amber-800">
                          <i class="fas fa-exclamation-triangle text-amber-500 flex-shrink-0 mt-0.5"></i>
                          <span>
                              Aucun IBAN configuré pour votre société. Vos factures n'afficheront pas vos coordonnées bancaires.
                              <br>
                              <a href="/app/parametres/facturation" class="font-bold underline hover:text-amber-900 transition-colors">
                                  Configurer dans Paramètres › Facturation
                              </a>
                          </span>
                      </div>
                  </transition>
                  <!-- Acompte -->
                  <label class="mobile-label mt-3">
                      <i class="fas fa-hand-holding-usd mr-1"></i>Acompte demandé
                  </label>

                  <div class="flex gap-2 items-center">
                      <!-- Input d'abord -->
                      <div class="relative flex-1">
                          <input v-if="doc.paiement.acompte_mode === 'pct'"
                              v-model.number="doc.paiement.acompte_pct"
                              type="number" min="0" max="100"
                              class="ipt ipt-mobile pr-7" placeholder="0" />
                          <input v-else
                              v-model.number="doc.paiement.acompte_eur"
                              type="number" min="0"
                              class="ipt ipt-mobile pr-7" placeholder="0" />
                          <span class="absolute right-3 top-3 text-xs text-gray-400 pointer-events-none">
                              {{ doc.paiement.acompte_mode === 'pct' ? '%' : '€' }}
                          </span>
                      </div>

                      <!-- Toggle ensuite -->
                      <div class="flex bg-gray-100 rounded-xl p-0.5 flex-shrink-0">
                          <button v-for="m in ['pct', 'eur']" :key="m"
                              @click="doc.paiement.acompte_mode = m"
                              :class="['px-3 py-2 rounded-lg text-xs font-bold transition-all',
                                  doc.paiement.acompte_mode === m
                                      ? 'bg-white text-orange-500 shadow-sm'
                                      : 'text-gray-400']">
                              {{ m === 'pct' ? '%' : '€' }}
                          </button>
                      </div>
                  </div>

                  <!-- Calcul en euros si mode pct -->
                  <transition name="fade">
                      <p v-if="doc.paiement.acompte_mode === 'pct' && doc.paiement.acompte_pct > 0 && totalTTC > 0"
                          class="text-xs text-gray-400 mt-1.5 tabular-nums">
                          {{ doc.paiement.acompte_pct }}% de {{ fmt(totalTTC) }} € TTC
                          = <strong class="text-orange-500">{{ fmt(acompte) }} €</strong>
                      </p>
                  </transition>
                </div>
              </div>
              <div>
                <label class="mobile-label"><i class="fas fa-sticky-note mr-1"></i>Notes supplémentaires</label>
                <textarea v-model="doc.note" rows="5" class="ipt ipt-mobile resize-none" placeholder="Conditions particulières, remarques…"></textarea>
              </div>
            </div>

            <!-- ── BOUTON VALIDER (desktop) ── -->
            <div class="hidden sm:block pb-10">
              <button @click="submit" :disabled="loadingSave"
                class="w-full py-4 bg-orange-500 hover:bg-orange-600 disabled:opacity-40 text-white font-black text-base rounded-2xl transition-colors shadow-lg shadow-orange-100 flex items-center justify-center gap-3">
                <i v-if="loadingSave" class="fas fa-circle-notch fa-spin"></i>
                <i v-else class="fas fa-check"></i>
                Valider le {{ doc.type.toLowerCase() }}
                <span class="font-normal opacity-70 text-sm">· {{ fmt(totalTTC) }} € TTC</span>
              </button>
            </div>

            <!-- Espace en bas sur mobile pour la barre sticky -->
            <div class="sm:hidden h-28"></div>

          </div>
        </div>

        <!-- ── VUE CONSULTER / APERÇU ── -->
        <div v-show="activeTab === 'consulter'" class="flex-1 overflow-y-auto bg-gray-200">
          <div class="p-4 sm:p-8">
            <div class="bg-white rounded-xl shadow-2xl max-w-2xl mx-auto overflow-hidden text-sm text-gray-800">

              <!-- Header -->
              <div class="px-6 sm:px-10 pt-8 sm:pt-10 pb-6">
                <div class="flex flex-col sm:flex-row justify-between items-start gap-4 sm:gap-6">
                  <div>
                    <div class="flex items-center gap-3 mb-3">
                      <img v-if="societe.logo" :src="societe.logo" class="h-10 w-auto object-contain rounded" />
                      <p class="text-xl font-black text-gray-900">{{ societe.nom || 'Votre entreprise' }}</p>
                    </div>
                    <div class="text-xs text-gray-500 space-y-0.5">
                      <p v-if="societe.adresse"><i class="fas fa-map-marker-alt mr-1.5 text-gray-300"></i>{{ societe.adresse }}, {{ societe.code_postal }} {{ societe.ville }}</p>
                      <p v-if="societe.tel"><i class="fas fa-phone mr-1.5 text-gray-300"></i>{{ societe.tel }}</p>
                      <p v-if="societe.mail"><i class="fas fa-envelope mr-1.5 text-gray-300"></i>{{ societe.mail }}</p>
                      <p v-if="societe.siret" class="font-mono text-gray-400 pt-0.5">SIRET {{ societe.siret }}</p>
                    </div>
                  </div>
                  <div class="text-right flex-shrink-0">
                    <p class="text-3xl sm:text-4xl font-black text-orange-500 tracking-tight leading-none">{{ doc.type }}</p>
                    <div class="mt-2 text-xs text-gray-500 space-y-0.5">
                      <p>Émis le <strong class="text-gray-800">{{ fmtDate(doc.date_emission) }}</strong></p>
                      <p v-if="doc.date_validite">{{ doc.type === 'FACTURE' ? 'Date limite de paiement' : "Valide jusqu'au" }} <strong class="text-gray-800">{{ fmtDate(doc.date_validite) }}</strong></p>
                    </div>
                    <span :class="['inline-flex items-center gap-1 mt-2 text-xs font-bold px-2.5 py-1 rounded-full border', currentStatut.badgeClass]">
                      <i :class="['fas text-xs', currentStatut.icon]"></i>{{ currentStatut.label }}
                    </span>
                  </div>
                </div>
                <div class="mt-6 h-0.5 rounded-full bg-orange-500"></div>
              </div>

              <!-- Client -->
              <div class="px-6 sm:px-10 pb-6">
                <div class="bg-orange-50 border border-orange-100 rounded-xl px-5 py-4">
                  <p class="text-xs font-black text-orange-400 uppercase tracking-widest mb-2">Facturé à</p>
                  <p class="font-black text-gray-900 text-lg">{{ client.nom || '—' }}</p>
                  <div class="text-xs text-gray-500 mt-1.5 space-y-0.5">
                    <p v-if="client.adresse">{{ client.adresse }}</p>
                    <p v-if="client.cp || client.ville">{{ client.cp }} {{ client.ville }}</p>
                    <p v-if="client.telephone"><i class="fas fa-phone mr-1 text-orange-300"></i>{{ client.telephone }}</p>
                    <p v-if="client.email"><i class="fas fa-envelope mr-1 text-orange-300"></i>{{ client.email }}</p>
                  </div>
                </div>
              </div>

              <!-- Tableau (scrollable sur mobile) -->
              <div class="px-6 sm:px-10 pb-6">
                <div class="overflow-x-auto -mx-2 px-2" style="-webkit-overflow-scrolling:touch">
                  <table class="w-full text-xs table-fixed rounded-xl overflow-hidden shadow-sm" style="min-width:420px">
                    <colgroup>
                      <col /><col style="width:36px"/><col style="width:52px"/>
                      <col style="width:72px"/><col style="width:44px"/><col style="width:84px"/>
                    </colgroup>
                    <thead>
                      <tr class="bg-slate-800 text-white text-left">
                        <th class="px-3 py-2.5 font-bold rounded-tl-lg">Désignation</th>
                        <th class="px-1 py-2.5 font-bold text-center">Qté</th>
                        <th class="px-1 py-2.5 font-bold text-center">Unité</th>
                        <th class="px-1 py-2.5 font-bold text-right">PU HT</th>
                        <th class="px-1 py-2.5 font-bold text-right">TVA</th>
                        <th class="px-3 py-2.5 font-bold text-right rounded-tr-lg">Total HT</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-50">
                      <tr v-if="!doc.lignes.length">
                        <td colspan="6" class="py-8 text-center text-gray-300 italic">Aucune prestation</td>
                      </tr>
                      <tr v-for="l in doc.lignes" :key="l.id" :class="l.is_remise ? 'bg-emerald-50/60' : ''">
                        <td class="px-3 py-2.5">
                          <p :class="['font-semibold leading-snug', l.is_remise ? 'text-emerald-700' : 'text-gray-800']">{{ l.designation || '—' }}</p>
                          <p v-if="l.detail" class="text-gray-400" style="font-size:10px">{{ l.detail }}</p>
                        </td>
                        <td class="px-1 py-2.5 text-center text-gray-500 tabular-nums">{{ l.qte }}</td>
                        <td class="px-1 py-2.5 text-center text-gray-400">{{ l.unite }}</td>
                        <td class="px-1 py-2.5 text-right text-gray-500 tabular-nums">{{ fmt(l.pu_ht) }} €</td>
                        <td class="px-1 py-2.5 text-right text-gray-400">{{ l.tva }}%</td>
                        <td :class="['px-3 py-2.5 text-right font-bold tabular-nums', l.is_remise ? 'text-emerald-600' : 'text-gray-900']">{{ fmt(l.total_ht) }} €</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Totaux aperçu -->
              <div class="px-6 sm:px-10 pb-6 flex justify-end">
                <div class="w-56 space-y-1.5 text-xs">
                  <div class="flex justify-between text-gray-500">
                    <span>Total HT</span><span class="tabular-nums">{{ fmt(totalHT) }} €</span>
                  </div>
                  <div v-for="t in tvaDetail" :key="t.taux" class="flex justify-between text-gray-400">
                    <span>TVA {{ t.taux }}%</span><span class="tabular-nums">{{ fmt(t.montant) }} €</span>
                  </div>
                  <div class="pt-2.5 border-t-2 border-slate-800">
                    <span class="font-black text-slate-900 text-sm">TOTAL TTC</span>
                    <div class="font-black text-orange-500 text-xl tabular-nums">{{ fmt(totalTTC) }} €</div>
                  </div>
                  <template v-if="doc.paiement.acompte_pct > 0">
                    <div class="flex justify-between text-gray-400">
                      <span>Acompte ({{ doc.paiement.acompte_pct }}%)</span>
                      <span class="tabular-nums">{{ fmt(acompte) }} €</span>
                    </div>
                    <div class="flex justify-between font-bold text-gray-700">
                      <span>Solde</span><span class="tabular-nums">{{ fmt(solde) }} €</span>
                    </div>
                  </template>
                </div>
              </div>

              <!-- Paiement + note -->
              <div class="mx-6 sm:mx-10 mb-6 border border-gray-100 rounded-xl flex flex-col sm:flex-row text-xs divide-y sm:divide-y-0 divide-x-0 sm:divide-x divide-gray-100">
                <div class="flex-1 px-4 py-3">
                  <p class="font-black text-gray-600 uppercase tracking-wider mb-1.5" style="font-size:10px">
                    <i class="fas fa-university mr-1 text-orange-400"></i>Règlement
                  </p>
                  <p class="text-gray-500">{{ payLabel }}</p>
                  <p v-if="doc.paiement.mode === 'VIREMENT' && doc.paiement.iban" class="font-mono text-gray-400 mt-0.5 break-all" style="font-size:10px">{{ doc.paiement.iban }}</p>
                </div>
                <div v-if="doc.note" class="flex-1 px-4 py-3">
                  <p class="font-black text-gray-600 uppercase tracking-wider mb-1.5" style="font-size:10px">
                    <i class="fas fa-info-circle mr-1 text-orange-400"></i>Notes
                  </p>
                  <p class="text-gray-500 leading-relaxed whitespace-pre-line">{{ doc.note }}</p>
                </div>
              </div>

              <!-- Signature devis -->
              <div v-if="doc.type === 'DEVIS'" class="mx-6 sm:mx-10 mb-6 border border-dashed border-gray-200 rounded-xl p-5">
                <p class="text-xs font-bold text-gray-400 mb-3">
                  <i class="fas fa-pen-nib mr-1.5"></i>Bon pour accord — Signature client
                </p>
                <div class="h-12 border-b border-dashed border-gray-200"></div>
                <p class="text-xs text-gray-300 mt-2">Date : ___________________________</p>
              </div>

              <!-- Footer -->
              <div class="bg-gray-50 border-t border-gray-100 px-6 sm:px-10 py-3 text-center" style="font-size:10px;color:#9ca3af">
                {{ societe.nom }}<span v-if="societe.siret"> — SIRET {{ societe.siret }}</span>
                <span v-if="societe.num_capacite"> — N° capacité {{ societe.num_capacite }}</span>
              </div>
            </div>
          </div>
        </div>

      </div><!-- fin corps -->

      <!-- ══ BARRE MOBILE STICKY BAS (saisie uniquement) ══ -->
      <div v-if="activeTab === 'saisie'" class="sm:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-2xl">
        <div class="flex items-center gap-3 px-4 py-3 pb-safe">
          <!-- Mini total -->
          <div class="flex-1">
            <p class="text-xs text-gray-400 font-medium leading-tight">Total TTC</p>
            <p class="text-2xl font-black text-orange-500 tabular-nums leading-tight">{{ fmt(totalTTC) }} €</p>
          </div>
          <!-- Bouton valider -->
          <button @click="submit" :disabled="loadingSave"
            class="flex-1 py-4 bg-orange-500 active:bg-orange-600 disabled:opacity-40 text-white font-black text-base rounded-2xl transition-colors shadow-lg shadow-orange-100 flex items-center justify-center gap-2">
            <i v-if="loadingSave" class="fas fa-circle-notch fa-spin"></i>
            <i v-else class="fas fa-check-circle text-xl"></i>
            <span>{{ invoiceId ? 'Modifier' : 'Valider' }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- ══ MODALE : SAUVEGARDER AU CATALOGUE ══ -->
    <transition name="fade">
    <div v-if="catalogueModal.show"
        class="fixed inset-0 z-50 flex items-center justify-center px-4">

        <div class="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
            @click="catalogueModal.show = false"></div>

        <div class="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden z-10">

            <div class="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <div class="flex items-center gap-2">
                    <i class="fas fa-bookmark text-orange-500"></i>
                    <span class="font-bold text-gray-900 text-sm">Ajouter au catalogue</span>
                </div>
                <button @click="catalogueModal.show = false"
                    class="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400">
                    <i class="fas fa-times text-xs"></i>
                </button>
            </div>

            <div class="px-5 py-4 space-y-4">

                <!-- Aperçu -->
                <div class="bg-gray-50 rounded-xl px-4 py-3 border border-gray-100">
                    <p class="font-semibold text-gray-800 text-sm">
                        {{ catalogueModal.line && catalogueModal.line.designation || '—' }}
                    </p>
                    <p v-if="catalogueModal.line && catalogueModal.line.detail"
                        class="text-xs text-gray-400 mt-0.5">
                        {{ catalogueModal.line.detail }}
                    </p>
                    <div class="flex gap-3 mt-2 text-xs text-gray-400">
                        <span>{{ catalogueModal.line && catalogueModal.line.pu_ht }} € HT</span>
                        <span>·</span>
                        <span>TVA {{ catalogueModal.line && catalogueModal.line.tva }} %</span>
                        <span>·</span>
                        <span>{{ catalogueModal.line && catalogueModal.line.unite }}</span>
                    </div>
                </div>

                <!-- Catégorie -->
                <div>
                    <label class="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
                        Catégorie
                    </label>

                    <div class="space-y-1 max-h-40 overflow-y-auto mb-2">
                        <button
                            v-for="cat in catalogueModal.categories"
                            :key="cat.id"
                            @click="catalogueModal.catId = cat.id; catalogueModal.showNewCat = false"
                            :class="[
                                'w-full flex items-center gap-2.5 px-3 py-2 rounded-xl border-2 text-left transition-all text-sm',
                                catalogueModal.catId === cat.id
                                    ? 'border-orange-400 bg-orange-50'
                                    : 'border-gray-100 hover:border-gray-200'
                            ]">
                            <span :class="['w-7 h-7 rounded-lg flex items-center justify-center text-xs flex-shrink-0', cat.color]">
                                <i :class="cat.icon || 'fas fa-tag'"></i>
                            </span>
                            <span class="font-medium text-gray-700">{{ cat.nom }}</span>
                            <i v-if="catalogueModal.catId === cat.id"
                                class="fas fa-check text-orange-500 ml-auto text-xs"></i>
                        </button>
                    </div>

                    <button
                        v-if="!catalogueModal.showNewCat"
                        @click="catalogueModal.showNewCat = true; catalogueModal.catId = null"
                        class="w-full flex items-center gap-2 px-3 py-2 rounded-xl border-2 border-dashed border-gray-200 hover:border-orange-300 hover:bg-orange-50 text-xs text-gray-400 hover:text-orange-500 font-medium transition-all">
                        <i class="fas fa-plus text-xs"></i>
                        Nouvelle catégorie
                    </button>

                    <div v-else class="flex items-center gap-2">
                        <input
                            v-model="catalogueModal.newCatName"
                            type="text"
                            placeholder="Nom de la catégorie…"
                            class="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-orange-400"
                            @keydown.esc="catalogueModal.showNewCat = false" />
                        <button
                            @click="catalogueModal.showNewCat = false; catalogueModal.newCatName = ''"
                            class="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 text-gray-400">
                            <i class="fas fa-times text-xs"></i>
                        </button>
                    </div>
                </div>
            </div>

            <div class="px-5 pb-5 flex gap-2">
                <button @click="catalogueModal.show = false"
                    class="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-500 hover:bg-gray-50 font-medium">
                    Annuler
                </button>
                <button
                    @click="saveToCatalogue"
                    :disabled="catalogueModal.saving || (!catalogueModal.catId && !catalogueModal.newCatName.trim())"
                    class="flex-1 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 disabled:opacity-40 text-white text-sm font-bold flex items-center justify-center gap-2">
                    <i v-if="catalogueModal.saving" class="fas fa-circle-notch fa-spin text-xs"></i>
                    <i v-else class="fas fa-bookmark text-xs"></i>
                    Sauvegarder
                </button>
            </div>
        </div>
    </div>
    </transition>

  </div>
</template>

<script>
let _id = 1

export default {
  name: 'InvoiceForm',

  props: {
    clientId:   { type: [Number, String], required: true },
    adresseId:  { type: [Number, String], required: true },   // ← nouveau, obligatoire
    chantierId: { type: [Number, String], default: null },    // ← optionnel (était required: false)
    invoiceId:  { type: [Number, String], default: null },
  },

  data() {
    const today = new Date().toISOString().slice(0, 10)
    return {
      activeTab:     'saisie',
      loadingClient: false,
      loadingSave:   false,
      loadingInvoice: false,
      showCatalogue: false,
      activeCat:     'maindoeuvre',
      showRemise:    false,
      remiseMode:    'pct',
      remiseValeur:  0,
      remiseLabel:   'Remise commerciale',

      client: { nom: '', adresse: '', cp: '', ville: '', telephone: '', email: '' },

      statuts: [
        { value: 'draft',      label: 'Brouillon',   icon: 'fa-pencil-alt',     selectClass: 'bg-gray-100 text-gray-600 border-gray-200',         badgeClass: 'bg-gray-100 text-gray-500 border-gray-200' },
        { value: 'issued',     label: 'Prêt à envoyer',     icon: 'fa-check',          selectClass: 'bg-emerald-50 text-emerald-600 border-emerald-200', badgeClass: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
        { value: 'sent',       label: 'Envoyé',     icon: 'fa-paper-plane',    selectClass: 'bg-blue-50 text-blue-600 border-blue-200',          badgeClass: 'bg-blue-50 text-blue-600 border-blue-200' },
        { value: 'overdue',    label: 'En retard',   icon: 'fa-clock',          selectClass: 'bg-yellow-50 text-yellow-600 border-yellow-200',    badgeClass: 'bg-yellow-50 text-yellow-600 border-yellow-200' },
        { value: 'paid',       label: 'Payé',       icon: 'fa-check-double',   selectClass: 'bg-orange-50 text-orange-600 border-orange-200',    badgeClass: 'bg-orange-50 text-orange-600 border-orange-200' },
        { value: 'cancelled',  label: 'Annulé',     icon: 'fa-ban',            selectClass: 'bg-red-50 text-red-500 border-red-200',             badgeClass: 'bg-red-50 text-red-500 border-red-200' },
        { value: 'accepted',   label: 'Accepté',     icon: 'fa-check-circle',   selectClass: 'bg-emerald-50 text-emerald-600 border-emerald-200', badgeClass: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
        { value: 'refused',    label: 'Refusé',      icon: 'fa-times-circle',   selectClass: 'bg-red-50 text-red-500 border-red-200',             badgeClass: 'bg-red-50 text-red-500 border-red-200' },
      ],

      catalogue: [],
      activeCat: null,

      doc: {
        type:           'DEVIS',
        numero:         '',
        statut:         'draft',
        date_emission:  today,
        date_validite:  new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString().slice(0, 10),
        lignes:         [],
        paiement: { mode: 'VIREMENT', iban: '', acompte_pct: 0, acompte_eur: 0, acompte_mode: 'pct' },
        note: '',
      },

      catalogueModal: {
        show:        false,
        line:        null,
        categories:  [],
        catId:       null,
        newCatName:  '',
        showNewCat:  false,
        saving:      false,
      },

      loadingGetData: false,
    }
  },

  async created() {
    this.loadingGetData = true

    // Pré-remplir l'IBAN depuis la société dès le départ
    if (this.societe.iban) {
        this.doc.paiement.iban = this.societe.iban
    }

    await this.fetchCatalogue()
    await this.fetchClient()
    if (this.invoiceId) {
        await this.fetchInvoice()
    }
    this.loadingGetData = false
  },

  computed: {
    societe()       { return this.$account?.account?.societe || {} },
    lignesActives() { return this.doc.lignes.filter(l => !l.is_remise) },
    totalHT()       { return this.doc.lignes.reduce((s, l) => s + (l.total_ht  || 0), 0) },
    totalTVA()      { return this.doc.lignes.reduce((s, l) => s + (l.total_tva || 0), 0) },
    totalTTC()      { return this.totalHT + this.totalTVA },
    totalCoutHT()   { return this.lignesActives.reduce((s, l) => s + (l.cout_ht || 0) * (l.qte || 1), 0) },
    totalMargeHT()  { return this.totalHT - this.totalCoutHT },
    tauxMarge() {
      if (this.totalHT <= 0) return '0,0'
      return this.fmt((this.totalMargeHT / this.totalHT) * 100)
    },
    tvaDetail() {
      const m = {}
      this.doc.lignes.forEach(l => {
        if (!m[l.tva]) m[l.tva] = { taux: l.tva, montant: 0 }
        m[l.tva].montant += l.total_tva || 0
      })
      return Object.values(m).filter(t => t.montant > 0).sort((a, b) => a.taux - b.taux)
    },
    acompte() {
      if (this.doc.paiement.acompte_mode === 'eur')
        return Math.min(this.doc.paiement.acompte_eur || 0, this.totalTTC)
      return this.totalTTC * ((this.doc.paiement.acompte_pct || 0) / 100)
    },
    solde()         { return this.totalTTC - this.acompte },
    currentStatut() { return this.statuts.find(s => s.value === this.doc.statut) || this.statuts[0] },
    payLabel()      { return { VIREMENT: 'Virement bancaire', CHEQUE: 'Chèque', ESPECES: 'Espèces', CB: 'Carte bancaire' }[this.doc.paiement.mode] || '' },
    remiseMontantHT() {
      const base = this.lignesActives.reduce((s, l) => s + (l.total_ht || 0), 0)
      return this.remiseMode === 'pct'
        ? Math.round(base * (this.remiseValeur || 0) / 100 * 100) / 100
        : Math.min(this.remiseValeur || 0, base)
    },
    payload() {
      return {
        type: this.doc.type, numero: this.doc.numero, statut: this.doc.statut,
        date_emission: this.doc.date_emission, date_validite: this.doc.date_validite,
        client_id:   this.clientId,
        adresse_id:  this.adresseId,   // ← nouveau
        lignes: this.doc.lignes.map(({ id, marge_montant, marge_eur, marge_mode, ...l }) => l),
        paiement: this.doc.paiement, note: this.doc.note,
        totaux: {
          total_ht:    +this.totalHT.toFixed(2),
          total_tva:   +this.totalTVA.toFixed(2),
          total_ttc:   +this.totalTTC.toFixed(2),
          total_cout:  +this.totalCoutHT.toFixed(2),
          marge_brute: +this.totalMargeHT.toFixed(2),
        },
      }
    },
    ibanAffiche() {
        return this.doc.paiement.iban || this.societe.iban || null
    },
  },

  methods: {
    // ── Logique calcul (inchangée) ──────────────────────────────
    calcFromCout(l) {
      if (l.cout_ht > 0) {
        if (l.marge_mode !== 'eur' && l.marge_pct > 0)
          l.pu_ht = Math.round(l.cout_ht * (1 + l.marge_pct / 100) * 100) / 100
        else if (l.marge_mode === 'eur' && l.marge_eur > 0)
          l.pu_ht = Math.round((l.cout_ht + l.marge_eur) * 100) / 100
        else
          l.pu_ht = l.cout_ht
      }
      this._calc(l)
    },
    calcFromMarge(l) {
      if (l.cout_ht > 0)
        l.pu_ht = Math.round(l.cout_ht * (1 + (l.marge_pct || 0) / 100) * 100) / 100
      this._calc(l)
    },
    calcFromMargeEur(l) {
      if (l.cout_ht > 0) {
        l.pu_ht     = Math.round((l.cout_ht + (l.marge_eur || 0)) * 100) / 100
        l.marge_pct = l.cout_ht > 0 ? Math.round(((l.marge_eur || 0) / l.cout_ht) * 100 * 10) / 10 : 0
      }
      this._calc(l)
    },
    calcFromPrix(l) {
      if (l.cout_ht > 0 && l.pu_ht > 0) {
        l.marge_pct = Math.round(((l.pu_ht - l.cout_ht) / l.cout_ht) * 100 * 10) / 10
        l.marge_eur = Math.round((l.pu_ht - l.cout_ht) * 100) / 100
      }
      this._calc(l)
    },
    _calc(l) {
      const brut      = (l.qte || 0) * (l.pu_ht || 0)
      l.total_ht      = Math.round(brut * 100) / 100
      l.total_tva     = Math.round(l.total_ht * ((l.tva || 0) / 100) * 100) / 100
      l.total_ttc     = Math.round((l.total_ht + l.total_tva) * 100) / 100
      l.marge_montant = l.cout_ht > 0 ? Math.round((l.pu_ht - l.cout_ht) * (l.qte || 1) * 100) / 100 : 0
    },

    // ── Fabrication de lignes ────────────────────────────────────
    makeLine(o = {}) {
      return {
        id: _id++, designation: '', detail: '', qte: 1, unite: 'u',
        cout_ht: 0, pu_ht: 0, marge_pct: 0, marge_eur: 0, marge_mode: 'pct', marge_montant: 0,
        tva: 10, total_ht: 0, total_tva: 0, total_ttc: 0, is_remise: false,
        ...o,
      }
    },
    addLine(type) {
      const d = {
        prestation: { designation: "Main d'œuvre", unite: 'h', pu_ht: 65, tva: 10 },
        materiau:   { designation: 'Fourniture',   unite: 'u', pu_ht: 0,  tva: 20 },
      }
      const l = this.makeLine(d[type] || {})
      this._calc(l)
      this.doc.lignes.push(l)
    },
    addLineVierge() { this.doc.lignes.push(this.makeLine()); this.showCatalogue = false },
    addFromCatalogue(item) {
      const ex = this.doc.lignes.find(l => l.designation === item.designation && !l.is_remise)
      if (ex) { ex.qte++; this._calc(ex) }
      else     { const l = this.makeLine(item); this._calc(l); this.doc.lignes.push(l) }
      this.showCatalogue = false
    },
    applyRemise() {
      if (this.remiseMontantHT <= 0) return
      const l = this.makeLine({ designation: this.remiseLabel || 'Remise', qte: 1, unite: 'forfait', pu_ht: -this.remiseMontantHT, tva: 0, is_remise: true })
      this._calc(l)
      const last = this.doc.lignes.reduce((i, line, idx) => !line.is_remise ? idx : i, -1)
      this.doc.lignes.splice(last + 1, 0, l)
      this.showRemise = false; this.remiseValeur = 0; this.remiseLabel = 'Remise commerciale'
    },
    openCatalogue() { this.activeCat = this.catalogue[0].id; this.showCatalogue = true },
    removeLine(idx) { this.doc.lignes.splice(idx, 1) },

    // ── Helpers UX mobile ────────────────────────────────────────
    incrementQty(l) {
      const step = l.unite === 'h' ? 0.5 : 1
      l.qte = Math.round((l.qte + step) * 10) / 10
      this.calcFromCout(l)
    },
    decrementQty(l) {
      const step = l.unite === 'h' ? 0.5 : 1
      l.qte = Math.max(0, Math.round((l.qte - step) * 10) / 10)
      this.calcFromCout(l)
    },
    toggleExpanded() {}, // gardé pour compatibilité éventuelle

    async openCatalogueModal(line) {
        this.catalogueModal.line       = line
        this.catalogueModal.catId      = null
        this.catalogueModal.newCatName = ''
        this.catalogueModal.showNewCat = false
        this.catalogueModal.saving     = false
        this.catalogueModal.show       = true

        if (!this.catalogueModal.categories.length) {
            const cats = await this.$request.post('catalogue/categories/list', {})
            this.catalogueModal.categories = cats || []
        }
    },

    async saveToCatalogue() {
        const m = this.catalogueModal
        if (m.saving) return
        m.saving = true
        try {
            let catId = m.catId
            if (!catId && m.newCatName.trim()) {
                const res = await this.$request.post('catalogue/categories/create', {
                    nom:   m.newCatName.trim(),
                    icon:  'fas fa-tag',
                    color: 'bg-orange-100 text-orange-600',
                })
                catId = (res.api ?? res).id
                m.categories.push(res.api ?? res)
            }
            if (!catId) return

            await this.$request.post('catalogue/lines/create', {
                categorie_id: catId,
                libelle:      m.line.designation || '',
                description:  m.line.detail      || '',
                qtt:          m.line.qte         ?? 1,
                unite:        m.line.unite        || 'u',
                cout_ht:      m.line.cout_ht      ?? 0,
                marge:        m.line.marge_pct    ?? 0,
                type_marge:   'pct',
                prix_ht:      m.line.pu_ht        ?? 0,
                prix_ttc:     m.line.total_ttc    ?? 0,
                tva:          m.line.tva          ?? 10,
            })

            this.$toast?.success('Ligne enregistrée dans le catalogue')
            // Refresh catalogue
            this.fetchCatalogue()
            m.show = false
        } catch {
            this.$toast?.error('Erreur lors de la sauvegarde.')
        } finally {
            m.saving = false
        }
    },

    // ── Formatage ────────────────────────────────────────────────
    fmt(v)     { return (v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) },
    fmtDate(d) { if (!d) return '—'; const [y, m, j] = d.split('-'); return `${j}/${m}/${y}` },

    // ── API ──────────────────────────────────────────────────────
    async fetchInvoice() {
      try {
        this.loadingInvoice = true
        const res = await this.$request.get(`/factures/${this.invoiceId}`)
        const form = typeof res.json_form === 'string' ? JSON.parse(res.json_form) : (res.json_form || {})

        // Restaure le document
        this.doc.type          = form.type          || 'DEVIS'
        this.doc.numero        = form.numero        || ''
        this.doc.statut        = form.statut        || 'draft'
        this.doc.date_emission = form.date_emission || new Date().toISOString().slice(0, 10)
        this.doc.date_validite = form.date_validite || ''
        this.doc.note          = form.note          || ''
        this.doc.paiement = {
            ...this.doc.paiement,
            ...(form.paiement || {}),
            // Si le doc sauvegardé n'a pas d'IBAN, on remet celui de la société
            iban: form.paiement?.iban || this.societe.iban || '',
        }
        this.doc.adresse_id     = form.adresse_id     || this.adresseId   // ← restauration de l'adresse_id dans doc

        // Restaure les lignes avec tous les champs calculés
        this.doc.lignes = (form.lignes || []).map(l => {
          const ligne = this.makeLine({
            ...l,
            marge_mode: 'pct',
            marge_eur:  l.cout_ht > 0 && l.pu_ht > l.cout_ht
              ? Math.round((l.pu_ht - l.cout_ht) * 100) / 100
              : 0,
            marge_pct:  l.marge_pct || (l.cout_ht > 0 && l.pu_ht > 0
              ? Math.round(((l.pu_ht - l.cout_ht) / l.cout_ht) * 100 * 10) / 10
              : 0),
          })
          this._calc(ligne)
          return ligne
        })

        await this.fetchClient()
      } catch (e) {
        this.$toast?.error('Impossible de charger le document.')
        console.error(e)
      } finally {
        this.loadingInvoice = false
      }
    },
    async fetchClient() {
      if (!this.clientId) return
      try {
        this.loadingClient = true
        const res = await this.$request.get(`/clients/${this.clientId}`)
        const c = res.api || res; const adr = c.adresses?.[0]
        this.client = {
          id: c.id, nom: [c.prenom, c.nom].filter(Boolean).join(' '),
          telephone: c.telephone || '', email: c.email || '',
          adresse: adr ? (adr.adresse1 || '') : (c.adresse1 || ''),
          cp:      adr ? (adr.code_postal || '') : (c.code_postal || ''),
          ville:   adr ? (adr.ville || '') : (c.ville || ''),
        }
      } catch { this.$toast?.error('Impossible de charger le client.') }
      finally  { this.loadingClient = false }
    },
    async submit() {
      if (!this.clientId) {
          this.$toast?.error('Client manquant.')
          return
      }
      try {
          this.loadingSave = true
          const body = {
              doc:         this.payload,
              client_id:   this.clientId,    // ← à la racine EN PLUS de dans doc
              adresse_id:  this.adresseId,
              chantier_id: this.chantierId,
          }
          if (this.invoiceId) body.invoice_id = this.invoiceId
          const res = await this.$request.post('/factures/save-local-invoice', body)
          this.$emit('submit', res)
      } catch { this.$toast?.error('Erreur lors de la création.') }
      finally  { this.loadingSave = false }
  },
    async fetchCatalogue() {
      const [cats, lns] = await Promise.all([
        this.$request.post('catalogue/categories/list', {}),
        this.$request.post('catalogue/lines/list', {}),
      ])

      this.catalogue = (cats || []).map(cat => ({
        id:         cat.id,
        nom:        cat.nom,
        icon:       cat.icon  || 'fas fa-tag',
        colorClass: cat.color || 'bg-orange-100 text-orange-600',
        items: (lns || [])
          .filter(l => String(l.categorie_id) === String(cat.id))
          .map(l => ({
            designation: l.libelle      || '',
            detail:      l.description  || '',
            qte:         parseFloat(l.qtt)      || 1,
            unite:       l.unite                || 'u',
            cout_ht:     parseFloat(l.cout_ht)  || 0,
            pu_ht:       parseFloat(l.prix_ht)  || 0,
            marge_pct:   parseFloat(l.marge)    || 0,
            tva:         parseFloat(l.tva)      || 20,
          })),
      }))

      if (this.catalogue.length) {
        this.activeCat = this.catalogue[0].id
      }
    },
  },
}
</script>

<style scoped>
/* ── Inputs globaux ─────────────────────────────────────── */
.ipt {
  display: block; width: 100%; background: #fff;
  border: 1px solid #e5e7eb; border-radius: 0.5rem;
  padding: 0.45rem 0.65rem; font-size: 0.8125rem;
  color: #374151; outline: none; font-family: inherit;
  transition: border-color .15s, box-shadow .15s;
}
.ipt::placeholder { color: #d1d5db; }
.ipt:focus { border-color: #f97316; box-shadow: 0 0 0 2px rgba(249,115,22,.15); }

/* Input mobile : plus grand, plus facile à tapper */
.ipt-mobile {
  padding: 0.65rem 0.75rem;
  font-size: 0.9375rem;
  border-radius: 0.75rem;
  min-height: 48px;
}

/* ── Table inputs (desktop) ─────────────────────────────── */
.ti {
  display: block; width: 100%; background: #fff;
  border: 1px solid #e5e7eb; border-radius: 0.3rem;
  padding: 0.28rem 0.35rem; font-size: 0.72rem; line-height: 1.4;
  color: #374151; outline: none; font-family: inherit;
  min-width: 0; box-sizing: border-box;
  transition: border-color .1s, box-shadow .1s;
}
.ti::placeholder { color: #d1d5db; }
.ti:hover  { border-color: #d1d5db; }
.ti:focus  { border-color: #f97316; box-shadow: 0 0 0 2px rgba(249,115,22,.12); }

/* Symbole €/% dans les cellules desktop */
.unit-sym {
  position: absolute; right: 5px; top: 50%; transform: translateY(-50%);
  font-size: 10px; color: #9ca3af; pointer-events: none; line-height: 1;
}

/* ── Boutons toggle marge %/€ (desktop) ────────────────── */
.mode-btn {
  flex-shrink: 0; width: 22px; height: 20px; border-radius: 4px;
  font-size: 10px; font-weight: 800; border: none; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: background .1s, color .1s;
}
.mode-btn-on  { background: #f97316; color: #fff; }
.mode-btn-off { background: #f3f4f6; color: #9ca3af; }
.mode-btn-off:hover { background: #e5e7eb; color: #6b7280; }

/* ── Boutons toggle marge (mobile, plus grands) ─────────── */
.mode-btn-mobile {
  flex-shrink: 0; width: 28px; height: 28px; border-radius: 6px;
  font-size: 11px; font-weight: 800; border: none; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: background .1s, color .1s;
}

/* ── Raccourcis bas de tableau (desktop) ────────────────── */
.shortcut-btn {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 11px; font-weight: 600; color: #9ca3af;
  background: none; border: none; cursor: pointer; padding: 0;
  transition: color .12s;
}
.shortcut-btn i { font-size: 10px; }
.shortcut-btn:hover { color: #f97316; }

/* ── Labels ─────────────────────────────────────────────── */
.mobile-label {
  display: block;
  font-size: 0.6875rem;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 0.375rem;
}
.mobile-label-sm {
  display: block;
  font-size: 0.625rem;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.375rem;
}

/* Label de section dans les cartes mobile */
.mobile-field-label {
  font-size: 0.6875rem;
  font-weight: 800;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  display: block;
}

/* ── Input désignation mobile ───────────────────────────── */
.mobile-desig-input {
  display: block;
  width: 100%;
  font-size: 1rem;
  font-weight: 700;
  background: transparent;
  border: none;
  border-bottom: 1.5px dashed #e5e7eb;
  outline: none;
  padding-bottom: 0.25rem;
  line-height: 1.3;
}
.mobile-desig-input:focus {
  border-bottom-color: #f97316;
}

/* ── Catalogue bottom sheet height ─────────────────────── */
.catalogue-sheet {
  max-height: 85vh;
}
@media (min-width: 640px) {
  .catalogue-sheet {
    max-height: 80vh;
  }
}

/* ── Safe area pour iPhone notch ────────────────────────── */
.pb-safe {
  padding-bottom: calc(1rem + env(safe-area-inset-bottom, 0px));
}

/* ── Scroll saisie avec espace pour barre sticky mobile ── */
@media (max-width: 639px) {
  .saisie-scroll {
    /* Le contenu s'arrête avant la barre sticky de 88px */
    padding-bottom: 0;
  }
}

/* ── Désactiver le zoom auto sur les inputs iOS ─────────── */
@media (max-width: 639px) {
  input[type="number"],
  input[type="text"],
  input[type="date"],
  select,
  textarea {
    font-size: 16px !important; /* iOS ne zoome pas si ≥ 16px */
  }
  /* Sauf les inputs de la table desktop qui sont cachés sur mobile */
}
</style>